import { useComputed } from '@preact/signals'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { useParams } from 'wouter-preact'

import { setSmartContractWorkspaceReadyState, smartContractsState } from 'state'
import { application } from 'state/application'

interface State {
	name: string
	active: boolean
	done: boolean
	warning?: string
}

const isReady = async (workspaceURL: string): Promise<boolean> => {
	const { data } = await application.value
		.forManagingDApps()
		.isWorkspaceHealthy(workspaceURL)

	// We can safely ignore error here
	// because it will never be return
	// error is converted into a truthy value

	return data!
}

const DEFAULT_WK_STATES: State[] = [
	{ name: 'Setting up workspace', active: true, done: false },
	{ name: 'Initializing environment', active: false, done: false },
	{ name: 'Finalizing', active: false, done: false }
]

const useWorkspace = () => {
	const params = useParams()
	const [ready, setReady] = useState(false)
	const [envId, setEnvId] = useState<string>()
	const [wkStates, setWkStates] = useState(DEFAULT_WK_STATES)

	const smartContract = useComputed(
		() =>
			smartContractsState.value!.find(
				smrtContract => smrtContract.name === params.dAppName
			)!
	)

	const updateWkStates = (updates: Partial<State>, stateIndex: number) => {
		setWkStates(prevStates => {
			const newState = [...prevStates]
			newState[stateIndex] = { ...newState[stateIndex]!, ...updates }

			return newState
		})
	}

	useEffect(() => {
		if (smartContract.value.isWorkspaceReady) {
			updateWkStates({ done: true }, 0)
			updateWkStates({ done: true, active: false }, 1)
			updateWkStates({ active: true }, 2)

			setEnvId(smartContract.value.webIdeUrl)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [smartContract.value.isWorkspaceReady])

	const init = useCallback(async () => {
		// Workspace is ready, no need to open it
		if (await isReady(smartContract.value.webIdeUrl)) {
			updateWkStates({ done: true }, 0)
			updateWkStates({ done: true, active: false }, 1)
			updateWkStates({ active: true }, 2)

			setEnvId(smartContract.value.webIdeUrl)
			setSmartContractWorkspaceReadyState(smartContract.value.id, true)
			return
		}

		// This happens when the smart contract has been created but
		// the workspace has not been opened
		const { error } = await application.value
			.forManagingDApps()
			.openWorkspace(smartContract.value.id)

		if (error) {
			// TODO: Emit error
			console.error('error', error)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		// Restart workspace when user changes the smart contract
		if (smartContract.value.isWorkspaceReady) {
			setWkStates(DEFAULT_WK_STATES)
			setSmartContractWorkspaceReadyState(smartContract.value.id, false)
		}

		if (smartContract.value) init()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [init, params.dAppName])

	useEffect(() => {
		if (wkStates[1]!.done) {
			setReady(true)
		}
	}, [wkStates])

	return {
		wkStates,
		ready,
		envId,
		smartContract: smartContract.value
	}
}

export { useWorkspace }
